import { replace } from 'react-router-dom';
import { match, P } from 'ts-pattern';
import { z } from 'zod';
import { zfd } from 'zod-form-data';
import { stateSchema } from '~/stores/app';
import Payments from '~/stores/payments';
import api from '~/utils/api';
import {
  pendingClaimLineItemToCalculatedLineItem,
  pendingClaimsToClaimLineItemCreate,
} from '~/utils/claimLineItem';
import { ClaimType } from '~/utils/constants';
import { logError } from '~/utils/observability';
import { makeAction, makeLoader, redirect } from '~/utils/routing';
import { extractSectionSummaryLabel } from '~/utils/summary';
import { AsyncReturnType } from '~/utils/types';

const stateCrewClaimSchema = stateSchema.shape.crewClaim.unwrap();
const contextSchema = z.object({
  settings: stateSchema.shape.settings.unwrap(),
  order: stateSchema.shape.order.unwrap(),
  crewClaim: stateCrewClaimSchema.extend({
    address: stateCrewClaimSchema.shape.address.unwrap(),
    claimLineItems: stateCrewClaimSchema.shape.claimLineItems.min(1),
  }),
});

type Context = z.infer<typeof contextSchema>;
type CalculationsResponse = AsyncReturnType<typeof getClaimCalculations>;

type Payment = NonNullable<CalculationsResponse['paymentIntent']> & {
  label: string;
};
const paymentSchema: z.ZodSchema<Payment> = z.object({
  clientSecret: z.string(),
  idFromPlatform: z.string(),
  amount: z.number(),
  label: z.string(),
});

const getClaimCalculations = ({
  order,
  settings,
  crewClaim: { claimLineItems },
}: Context) =>
  api.calculateBeforeClaimSubmission({
    params: { storefrontId: settings.storefrontId },
    body: {
      id: order.id,
      claimType: ClaimType.warranty,
      calculatedLineItems: claimLineItems.map((claim) =>
        pendingClaimLineItemToCalculatedLineItem(claim),
      ),
    },
  });

const setPaymentInformation = ({
  paymentIntent,
  deductions,
}: CalculationsResponse) => {
  if (!paymentIntent) {
    return { payment: undefined };
  }

  Payments.initializePayment(
    paymentIntent.clientSecret,
    paymentIntent.idFromPlatform,
  );

  return {
    payment: {
      ...paymentIntent,
      label: extractSectionSummaryLabel('FEE', deductions.sections),
    },
  };
};

const formatPageData =
  (context: Context) =>
  ({ payment }: ReturnType<typeof setPaymentInformation>) => ({
    payment,
    claims: context.crewClaim.claimLineItems,
  });

export const { loader, useLoaderData } = makeLoader(
  async ({ params }, appStore) =>
    match(contextSchema.safeParse(appStore.getState()))
      .with(
        { success: true, data: { crewClaim: { status: P.not('new') } } },
        () => replace(`/${params.store}/order/${params.idFromPlatform}`),
      )
      .with({ success: true, data: P.select() }, (context) =>
        getClaimCalculations(context)
          .then(setPaymentInformation)
          .then(formatPageData(context)),
      )
      .with({ success: false, error: P.select() }, (error) => {
        logError(
          new Error(`Error in warranty review loader: ${error.message}`),
        );
        return redirect(`/${params.store}/order/${params.idFromPlatform}`);
      })
      .exhaustive(),
);

const confirmPayment = (payment?: Payment) =>
  payment ?
    Payments.confirmPayment().then((intentId) => ({
      ...payment,
      intentId,
    }))
  : Promise.resolve(null);

const createClaim =
  (context: Context) =>
  (confirmedPayment: AsyncReturnType<typeof confirmPayment>) => {
    const {
      settings: { storefrontId, stripeConfig },
      order: { id, email, currencyCode },
      crewClaim: { claimLineItems, address },
    } = context;

    const customerPayment =
      (
        !!confirmedPayment?.intentId &&
        !!confirmedPayment?.amount &&
        !!stripeConfig
      ) ?
        {
          idFromPlatform: confirmedPayment.intentId,
          currencyCode,
          stripeConfig,
          //! amount should be in dollars or the ratio equivalent of the currency
          amount: confirmedPayment.amount,
        }
      : undefined;

    return api.createCrewClaim({
      params: { storefrontId },
      body: {
        feeName: confirmedPayment?.label,
        originalStoreOrderId: id,
        billingAddress: address,
        shippingAddress: address,
        customerEmail: email,
        claimType: ClaimType.warranty,
        claimLineItems: pendingClaimsToClaimLineItemCreate(claimLineItems),
        giftCardIncentiveAmountApplied: 0,
        feeApplied: 0,
        returnShippingAmountApplied: 0,
        exchangeOrderShippingAmountApplied: 0,
        source: 'Customer_App',
        customerPayment,
      },
    });
  };

const formDataSchema = zfd.formData({
  payment: zfd.json(paymentSchema.optional()),
});

export const { action, useActionResult } = makeAction(
  ({ params: { store }, formData }, appStore) =>
    match({
      context: contextSchema.safeParse(appStore.getState()),
      formData: formDataSchema.safeParse(formData),
    })
      .with(
        {
          context: { success: true, data: P.select('context') },
          formData: { success: true, data: P.select('data') },
        },
        ({ context, data: { payment } }) =>
          confirmPayment(payment)
            .then((paymentId) => {
              appStore.dispatch(appStore.actions.crewClaim.submitClaim());

              return createClaim(context)(paymentId).catch((err) => {
                appStore.dispatch(appStore.actions.crewClaim.unlockClaim());

                throw err;
              });
            })
            .then((claim) => {
              appStore.dispatch(appStore.actions.completeCrewClaim());

              return redirect(`/${store}/claim/overview/${claim.externalId}`);
            })
            .catch((err) => ({
              ok: false,
              message: err instanceof Error ? err.message : `${err}`,
            })),
      )
      .with(
        { context: { success: false, error: P.select() } },
        (error) =>
          new Error('Invalid state in Warranty Review action.', {
            cause: error,
          }),
      )
      .with(
        {
          formData: { success: false, error: P.select() },
        },
        (error) =>
          new Error('Invalid Warranty review form data.', { cause: error }),
      )
      .exhaustive(),
);
